import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { notify } from '@/components/ui/toast'
import { errorText } from '@/i18n/feedback'
import { useT } from '@/i18n/use-t'
import { ApiError, UnknownOutcomeError } from './api/client'
import { currentIdentity, isCurrentIdentity } from './api/identity-state'
import { type EngagementKind, readEngagement, writeEngagement } from './api/library'
import { patchEntity } from './queries'
import { SESSION_KEY } from './session'

export type { EngagementKind } from './api/library'
export type EngagementPhase = 'idle' | 'pending' | 'unknown' | 'verifying'

const FIELD: Record<EngagementKind, 'favorited' | 'subscribed'> = {
  favorite: 'favorited',
  subscription: 'subscribed',
}

export function useEngagement(kind: EngagementKind, id: string) {
  const queryClient = useQueryClient()
  const t = useT()
  const [phase, setPhase] = useState<EngagementPhase>('idle')
  const inflight = useRef(false)

  const apply = useCallback(
    (active: boolean) => {
      patchEntity(queryClient, id, { [FIELD[kind]]: active })
      void queryClient.invalidateQueries({ queryKey: SESSION_KEY })
      if (kind === 'favorite')
        void queryClient.invalidateQueries({
          predicate: (q) => q.queryKey.includes('favorites') || q.queryKey.includes('videos'),
        })
      if (kind === 'subscription')
        void queryClient.invalidateQueries({
          predicate: (q) =>
            q.queryKey.includes('subscriptions') ||
            q.queryKey.includes('updates') ||
            q.queryKey.includes('actresses'),
        })
    },
    [id, kind, queryClient],
  )

  const verify = useCallback(async () => {
    if (inflight.current) return
    inflight.current = true
    setPhase('verifying')
    const identity = currentIdentity()
    try {
      const active = await readEngagement(kind, id)
      if (!isCurrentIdentity(identity)) return
      apply(active)
      setPhase('idle')
    } catch {
      if (isCurrentIdentity(identity)) setPhase('unknown')
    } finally {
      inflight.current = false
    }
  }, [apply, id, kind])

  const setActive = useCallback(
    async (active: boolean) => {
      if (inflight.current || phase === 'unknown') return
      inflight.current = true
      setPhase('pending')
      const identity = currentIdentity()
      try {
        await writeEngagement(kind, id, active)
        if (!isCurrentIdentity(identity)) return
        apply(active)
        setPhase('idle')
      } catch (error) {
        if (!isCurrentIdentity(identity)) return
        if (error instanceof UnknownOutcomeError) {
          setPhase('unknown')
          notify(t('common.unknownOutcome'), 'warn')
          inflight.current = false
          await verify()
          return
        }
        setPhase('idle')
        if (error instanceof ApiError && error.code === 'SUBSCRIPTION_LIMIT_REACHED')
          notify(t('actress.limitReached'), 'warn', t('today.quotaFull'))
        else notify(errorText(error, t), 'error')
      } finally {
        inflight.current = false
      }
    },
    [apply, id, kind, phase, t, verify],
  )

  return { phase, setActive, verify }
}
